import React, { useState, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Collapse,
  Paper,
  Divider,
} from "@mui/material";
import {
  ChevronRight,
  ExpandMore,
  FolderOutlined,
  FolderOpenOutlined,
  Check,
  AccountTree,
} from "@mui/icons-material";
import {
  SimpleDialog,
  SearchField,
  LoadingIndicator,
} from "../../../toolympus/components/primitives";
import { FormControlsForFields } from "../../../toolympus/components/schemed/FormControlsForFields";
import { CopTreeNode } from "./useCopTreeNodes";
import {
  CopDocumentNodeLink,
  CopDocumentNodeLinkSchema,
  TreeNodeWithChildren,
} from "./useCopDocumentNodeLinks";

interface Props {
  isOpen: boolean;
  close: () => void;
  rootNodes: TreeNodeWithChildren[];
  allNodes: CopTreeNode[];
  nodesById: Map<number, CopTreeNode>;
  linkedNodeIds: Set<number>;
  onAddLink: (payload: {
    node_id: number;
    relation_type?: string | null;
    anchor_id?: string | null;
  }) => Promise<void>;
}

const TreeNodeRow: React.FC<{
  node: TreeNodeWithChildren;
  depth: number;
  expandedIds: Set<number>;
  selectedTargetId: number | null;
  toggleExpand: (id: number) => void;
  onSelect: (nodeId: number) => void;
  linkedNodeIds: Set<number>;
}> = ({
  node,
  depth,
  expandedIds,
  selectedTargetId,
  toggleExpand,
  onSelect,
  linkedNodeIds,
}) => {
  const isExpanded = expandedIds.has(node._id);
  const isLinked = linkedNodeIds.has(node._id);
  const isSelected = selectedTargetId === node._id;
  const hasChildren = node.children.length > 0;

  return (
    <>
      <ListItemButton
        onClick={() => !isLinked && onSelect(node._id)}
        disabled={isLinked}
        selected={isSelected}
        sx={{
          pl: depth * 2.5 + 1,
          py: 0.5,
          borderBottom: "1px solid",
          borderColor: "action.hover",
          opacity: isLinked ? 0.6 : 1,
        }}
      >
        <Box sx={{ width: 24, display: "flex", alignItems: "center" }}>
          {hasChildren && (
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                toggleExpand(node._id);
              }}
              sx={{ p: 0.25 }}
            >
              {isExpanded ? (
                <ExpandMore fontSize="small" />
              ) : (
                <ChevronRight fontSize="small" />
              )}
            </IconButton>
          )}
        </Box>

        <ListItemIcon sx={{ minWidth: 26 }}>
          {node.color ? (
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: node.color,
                border: "1px solid rgba(0,0,0,0.2)",
              }}
            />
          ) : isExpanded ? (
            <FolderOpenOutlined sx={{ fontSize: 18 }} />
          ) : (
            <FolderOutlined sx={{ fontSize: 18 }} />
          )}
        </ListItemIcon>

        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                {node.title}
              </Typography>
              {node.type && (
                <Chip
                  label={node.type}
                  size="small"
                  sx={{ height: 16, fontSize: "0.65rem" }}
                />
              )}
            </Box>
          }
        />

        {isLinked && (
          <Chip
            size="small"
            icon={<Check sx={{ fontSize: "14px !important" }} />}
            label="Linked"
            color="success"
            variant="outlined"
            sx={{ height: 20, fontSize: "0.7rem" }}
          />
        )}
      </ListItemButton>

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {node.children.map(child => (
              <TreeNodeRow
                key={child._id}
                node={child}
                depth={depth + 1}
                expandedIds={expandedIds}
                selectedTargetId={selectedTargetId}
                toggleExpand={toggleExpand}
                onSelect={onSelect}
                linkedNodeIds={linkedNodeIds}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const AddTreeNodeLinkDialog: React.FC<Props> = ({
  isOpen,
  close,
  rootNodes,
  allNodes,
  nodesById,
  linkedNodeIds,
  onAddLink,
}) => {
  const [filterText, setFilterText] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<CopDocumentNodeLink>>({
    relation_type: "",
    anchor_id: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setFilterText("");
      setExpandedIds(new Set());
      setSelectedTargetId(null);
      setFormData({ relation_type: "", anchor_id: "" });
    }
  }, [isOpen]);

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredNodes = useMemo(() => {
    const term = filterText.trim().toLowerCase();
    if (!term) return [];

    return allNodes.filter(
      n =>
        n.title?.toLowerCase().includes(term) ||
        n.type?.toLowerCase().includes(term) ||
        String(n._id).includes(term)
    );
  }, [allNodes, filterText]);

  const handleSelect = (nodeId: number) => {
    if (linkedNodeIds.has(nodeId)) return;
    setSelectedTargetId(nodeId);
  };

  const handleDoSearch = () => {
    const validMatches = filteredNodes.filter(n => !linkedNodeIds.has(n._id));
    if (validMatches.length === 1) {
      handleSelect(validMatches[0]._id);
    }
  };

  const handleSave = async () => {
    if (!selectedTargetId) return;
    setIsSubmitting(true);
    try {
      await onAddLink({
        node_id: selectedTargetId,
        relation_type: formData.relation_type,
        anchor_id: formData.anchor_id,
      });
      close();
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedNode = selectedTargetId ? nodesById.get(selectedTargetId) : null;
  const isSearchMode = Boolean(filterText.trim());

  return (
    <SimpleDialog
      dialogTitle={
        <FormattedMessage
          id="cop_tree.link_node_title"
          defaultMessage="Link Tree Node to Document"
        />
      }
      isOpen={isOpen}
      close={close}
      save={handleSave}
      titleActions={
        isSubmitting ? <LoadingIndicator sizeVariant="s" color="primary" /> : undefined
      }
      saveDisabled={!selectedTargetId}
      maxWidth="md"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Step 1: Target Tree Node */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            1. Select Tree Node:
          </Typography>

          {selectedNode ? (
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "action.selected",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Check color="primary" fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedNode.title}
                </Typography>
                {selectedNode.type && (
                  <Chip label={selectedNode.type} size="small" sx={{ height: 18 }} />
                )}
              </Box>
              <Chip
                label="Change"
                size="small"
                onClick={() => setSelectedTargetId(null)}
                variant="outlined"
              />
            </Paper>
          ) : (
            <>
              <SearchField
                filter={filterText}
                setFilter={setFilterText}
                small
                autoFocus
                noButton
                doSearch={handleDoSearch}
              />

              <List
                dense
                disablePadding
                sx={{
                  maxHeight: 240,
                  overflowY: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                {isSearchMode ? (
                  filteredNodes.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        No matching tree nodes found
                      </Typography>
                    </Box>
                  ) : (
                    filteredNodes.map(node => {
                      const isLinked = linkedNodeIds.has(node._id);
                      return (
                        <ListItemButton
                          key={node._id}
                          onClick={() => handleSelect(node._id)}
                          disabled={isLinked}
                          sx={{
                            py: 0.75,
                            px: 1.5,
                            borderBottom: "1px solid",
                            borderColor: "action.hover",
                            opacity: isLinked ? 0.6 : 1,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <AccountTree fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={node.title} secondary={node.type} />
                          {isLinked && (
                            <Chip
                              size="small"
                              label="Linked"
                              color="success"
                              variant="outlined"
                              sx={{ height: 20, fontSize: "0.65rem" }}
                            />
                          )}
                        </ListItemButton>
                      );
                    })
                  )
                ) : (
                  rootNodes.map(node => (
                    <TreeNodeRow
                      key={node._id}
                      node={node}
                      depth={0}
                      expandedIds={expandedIds}
                      selectedTargetId={selectedTargetId}
                      toggleExpand={toggleExpand}
                      onSelect={handleSelect}
                      linkedNodeIds={linkedNodeIds}
                    />
                  ))
                )}
              </List>
            </>
          )}
        </Box>

        <Divider />

        {/* Step 2: Relation details */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            2. Relation Details (Optional):
          </Typography>

          <FormControlsForFields
            fields={[
              ["relation_type"]
            ]}
            schema={CopDocumentNodeLinkSchema}
            data={formData}
            onChange={(_orig, changes) =>
              setFormData(prev => ({ ...prev, ...changes }))
            }
          />
        </Box>
      </Box>
    </SimpleDialog>
  );
};